import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utility/axiosInstance";
import { handleAxiosError } from "../../helpers/handleAxiosError";

export interface VerifiedDomain {
    domain: string;
    status: 'pending' | 'verified';
    verified_at: string | null;
    instance_id: string | null;
    proxy_host_id: number | null;
}

/** One of the account's names that still serves a domain it asked to remove (ROADMAP 86: 409 DOMAIN_IN_USE). */
export interface DomainName {
    id: number;
    domain_names: string[];
    forward_host: string | null;
}

/** Why a removal was refused: the API's error envelope, and the names when the domain is still in use. */
export interface DomainRemovalRefusal {
    status: number;
    code: string | null;
    message: string;
    hint: string | null;
    names: DomainName[];
}

interface DomainsState {
    domains: VerifiedDomain[];
    loading: boolean;
    error: string | null;
}

const initialState: DomainsState = { domains: [], loading: false, error: null };

export const fetchDomains = createAsyncThunk("domains/fetch", async () => {
    const res = await axiosInstance.get("/v1/dns/domains");
    return res.data as VerifiedDomain[];
});

// A refused removal keeps its envelope, so the page can list the names that still serve the domain.
export const deleteDomain = createAsyncThunk<string, string, { rejectValue: DomainRemovalRefusal }>(
    "domains/delete",
    async (domain, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/v1/dns/domains/${encodeURIComponent(domain)}`);
            return domain;
        } catch (e: any) {
            const err = e?.response?.data?.error;
            return rejectWithValue({
                status: e?.response?.status ?? 0,
                code: typeof err?.code === "string" ? err.code : null,
                message: handleAxiosError(e),
                hint: typeof err?.hint === "string" ? err.hint : null,
                names: Array.isArray(err?.names) ? err.names : [],
            });
        }
    },
);

const domainsSlice = createSlice({
    name: "domains",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDomains.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchDomains.fulfilled, (s, a) => { s.loading = false; s.domains = a.payload; })
            .addCase(fetchDomains.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? "Failed"; })
            .addCase(deleteDomain.fulfilled, (s, a) => { s.domains = s.domains.filter(d => d.domain !== a.payload); });
    },
});

export default domainsSlice.reducer;
